import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { ScrollArea } from "../ui/scroll-area";
import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";

/**
 * Dialog to educate users about what makes a good example
 * Shows guidelines and criteria checkboxes (for reflection only, not stored)
 *
 * @typedef {Object} ExampleGuidelinesDialogProps
 * @property {boolean} open - Whether the dialog is open
 * @property {(open: boolean) => void} onOpenChange - Callback when dialog open state changes
 * @property {() => void} onContinue - Callback when user continues (no data passed)
 * @property {() => void} onCancel - Callback when user cancels
 * @property {boolean} [isActivityAuthor=false] - Whether the current user is the activity author
 *
 * @param {ExampleGuidelinesDialogProps} props
 */
const ExampleGuidelinesDialog = ({
  open,
  onOpenChange,
  onContinue,
  onCancel,
  isActivityAuthor = false,
}) => {
  const { t } = useTranslation();

  // Checkbox state (for validation only, not passed to parent)
  const [criteria, setCriteria] = useState({
    clearImpact: false,
    wellDocumented: false,
    interestingStory: false,
  });

  // Check if at least one checkbox is checked
  const canContinue =
    criteria.clearImpact ||
    criteria.wellDocumented ||
    criteria.interestingStory;

  const handleContinue = () => {
    onContinue();
    // Reset checkboxes for next time
    setCriteria({
      clearImpact: false,
      wellDocumented: false,
      interestingStory: false,
    });
  };

  const handleCancel = () => {
    onCancel();
    // Reset checkboxes
    setCriteria({
      clearImpact: false,
      wellDocumented: false,
      interestingStory: false,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-5 pb-2">
          <DialogTitle className="text-lg font-semibold">
            {t("example_creation_guidelines") || "Example Creation Guidelines"}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea
          className="flex-1 px-6 overflow-y-auto"
          style={{ maxHeight: "calc(85vh - 160px)" }}
        >
          <div className="space-y-4 pb-3 pr-4">
            {/* What Are Examples? */}
            <div className="space-y-1.5">
              <h3 className="text-sm font-semibold text-gray-900">
                {t("what_are_examples") || "What Are Examples?"}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                {t("what_are_examples_description") ||
                  "Examples show how others have used this activity to gain insights and solve real problems."}
              </p>
            </div>

            {/* Why Create an Example? */}
            <div className="space-y-1.5">
              <h3 className="text-sm font-semibold text-gray-900">
                {t("why_create_example") || "Why Create an Example?"}
              </h3>
              <ul className="space-y-1 text-sm text-gray-600">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>
                    {t("why_create_example_1") ||
                      "Help others learn from your experience"}
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>
                    {t("why_create_example_2") ||
                      "Deepen your own understanding of what happened"}
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>
                    {t("why_create_example_3") || "Contribute to the community"}
                  </span>
                </li>
              </ul>
            </div>

            {/* Is This a Good Example? */}
            <div className="space-y-2.5 pt-1">
              <div className="space-y-1">
                <h3 className="text-sm font-semibold text-gray-900">
                  {t("is_this_good_example") || "Is This a Good Example?"}
                </h3>
                <p className="text-xs text-gray-600">
                  {t("is_this_good_example_description") ||
                    "Only create an example if it meets one or more of these criteria:"}
                </p>
              </div>

              {/* Criteria Checkboxes */}
              <div className="space-y-2.5 pl-1">
                {/* Clear positive impact */}
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="criteria-impact"
                    checked={criteria.clearImpact}
                    onCheckedChange={(checked) =>
                      setCriteria({
                        ...criteria,
                        clearImpact: checked === true,
                      })
                    }
                    className="mt-2"
                  />
                  <div className="flex-1 space-y-0.5">
                    <Label
                      htmlFor="criteria-impact"
                      className="text-sm font-medium cursor-pointer leading-tight"
                    >
                      {t("criteria_clear_impact") || "Clear positive impact"}
                    </Label>
                    <p className="text-xs text-gray-600 leading-snug">
                      {t("criteria_clear_impact_description") ||
                        "The activity led to valuable insight or meaningful change in your situation"}
                    </p>
                  </div>
                </div>

                {/* Well-documented */}
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="criteria-documented"
                    checked={criteria.wellDocumented}
                    onCheckedChange={(checked) =>
                      setCriteria({
                        ...criteria,
                        wellDocumented: checked === true,
                      })
                    }
                    className="mt-2"
                  />
                  <div className="flex-1 space-y-0.5">
                    <Label
                      htmlFor="criteria-documented"
                      className="text-sm font-medium cursor-pointer leading-tight"
                    >
                      {t("criteria_well_documented") ||
                        "Well-documented and detailed"}
                    </Label>
                    <p className="text-xs text-gray-600 leading-snug">
                      {t("criteria_well_documented_description") ||
                        "Thoroughly explained with notes, prologue, and epilogue that others can follow"}
                    </p>
                  </div>
                </div>

                {/* Interesting story */}
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="criteria-story"
                    checked={criteria.interestingStory}
                    onCheckedChange={(checked) =>
                      setCriteria({
                        ...criteria,
                        interestingStory: checked === true,
                      })
                    }
                    className="mt-2"
                  />
                  <div className="flex-1 space-y-0.5">
                    <Label
                      htmlFor="criteria-story"
                      className="text-sm font-medium cursor-pointer leading-tight"
                    >
                      {t("criteria_interesting_story") ||
                        "Interesting or unique story"}
                    </Label>
                    <p className="text-xs text-gray-600 leading-snug">
                      {t("criteria_interesting_story_description") ||
                        "Unexpected use case or compelling narrative"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* What You Need to Do */}
            <div className="space-y-1.5 pt-1">
              <h3 className="text-sm font-semibold text-gray-900">
                {t("what_you_need_to_do") || "What You Need to Do:"}
              </h3>
              <ol className="space-y-1 text-sm text-gray-600 pl-5">
                <li className="list-decimal">
                  {t("what_you_need_to_do_1") ||
                    "Review the entire engagement carefully"}
                </li>
                <li className="list-decimal">
                  {t("what_you_need_to_do_2") ||
                    "Add prologue (your initial situation/problem)"}
                </li>
                <li className="list-decimal">
                  {t("what_you_need_to_do_3") ||
                    "Add epilogue (what happened, what you learned)"}
                </li>
                <li className="list-decimal">
                  {t("what_you_need_to_do_4") ||
                    "Add notes to clarify your thinking on each pane"}
                </li>
              </ol>
            </div>

            {/* Validation message */}
            {!canContinue && (
              <div className="flex items-center space-x-2 p-2.5 bg-primary/5 border border-primary/20 rounded-md">
                <Info className="w-4 h-4 text-primary flex-shrink-0" />
                <p className="text-xs text-primary leading-tight">
                  {t("at_least_one_criteria") ||
                    "Please select at least one criteria to continue"}
                </p>
              </div>
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="px-6 pb-5 pt-3 border-t">
          <Button
            type="button"
            onClick={handleContinue}
            disabled={!canContinue}
            className="w-full"
          >
            {t("continue") || "Continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExampleGuidelinesDialog;
